// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * Star Systems Configuration - Neon Metropolis Universe
 * 
 * Cyberpunk universe where megacorporations rule and neon bleeds through acid rain.
 */

export const starSystems = [
  {
    id: 1300,
    name: 'Neo-Tokyo Orbital',
    type: 'station',
    distance: 0,
    coordinates: { x: 45, y: 50, z: 0 }, // Visual center
    threat: 'MODERATE',
    resources: 'HIGH',
    services: ['refuel', 'repairs', 'trade', 'weapons', 'upgrades', 'modules'],
    tradeType: 'station_advanced',
    primaryColor: '#00ffff',
    secondaryColor: '#ff00ff',
    hasRings: false,
    description: 'Massive orbital arcology above Neo-Tokyo. Zaibatsu corporate headquarters pierce the neon-lit void. Cybernetic enhancement clinics on every level. Ramen shops serve synthetic protein. The future is here—it\'s just not evenly distributed.',
    scanDescription: 'SCAN: Orbital megastructure detected. Population: 50 million. Corporate security grid: ACTIVE. Cybernetic enhancement facilities: OPERATIONAL. Warning: Neural virus signatures detected in local networks. Recommend firewall updates.'
  },
  {
    id: 1301,
    name: 'Los Angeles 2099',
    type: 'planet',
    distance: 15,
    coordinates: { x: 60, y: 55, z: 3 },
    threat: 'MODERATE',
    resources: 'MEDIUM',
    services: ['refuel', 'exploration', 'repairs', 'trade'],
    tradeType: 'industrial',
    primaryColor: '#ff8800',
    secondaryColor: '#00ffff',
    hasRings: false,
    description: 'Perpetual rain falls on neon-lit streets. Replicants walk among humans. Blade Runners hunt rogue androids. Off-world colonies advertise on massive holoscreens. "A new life awaits you in the Off-world colonies!"',
    scanDescription: 'SCAN: Urban sprawl extends to horizon. Atmospheric composition: 40% smog. Replicant detection facilities detected. Tyrell Corporation presence confirmed. Warning: Voight-Kampff tests may be administered.'
  },
  {
    id: 1302,
    name: 'Night City',
    type: 'planet',
    distance: 22,
    coordinates: { x: 30, y: 60, z: -5 },
    threat: 'HIGH',
    resources: 'HIGH',
    services: ['refuel', 'exploration', 'repairs', 'trade', 'weapons'],
    tradeType: 'industrial',
    primaryColor: '#ffff00',
    secondaryColor: '#ff00ff',
    hasRings: false,
    description: 'City of dreams and chrome. Gangs control the streets: Tyger Claws, Maelstrom, Valentinos. Ripperdocs install illegal cyberware in back alleys. Braindance studios sell digital experiences. "In Night City, you can be anything—if you survive."',
    scanDescription: 'SCAN: Megacity detected. Gang territories mapped. Ripperdoc clinics: 847. Braindance studios: 1,203. Arasaka Tower dominates skyline. Warning: Cyberpsychosis incidents elevated. Recommend MaxTac avoidance protocols.'
  },
  {
    id: 1303,
    name: 'Chiba City Station',
    type: 'station',
    distance: 28,
    coordinates: { x: 55, y: 35, z: 8 },
    threat: 'LOW',
    resources: 'VERY HIGH',
    services: ['refuel', 'repairs', 'trade', 'weapons', 'upgrades', 'modules'],
    tradeType: 'station_advanced',
    primaryColor: '#00ff88',
    secondaryColor: '#00ffff',
    hasRings: true,
    description: 'The Sprawl\'s black market hub. Neuromancer AIs for sale. Illegal software and stolen data. Yakuza-controlled sectors. "Cyberspace. A consensual hallucination experienced daily by billions."',
    scanDescription: 'SCAN: Space station detected. Black market activity: EXTREME. AI constructs available for purchase. Yakuza presence confirmed. Warning: Stolen data transactions monitored by corporate security. Recommend encryption protocols.'
  },
  {
    id: 1304,
    name: 'New Hong Kong',
    type: 'planet',
    distance: 18,
    coordinates: { x: 40, y: 70, z: 2 },
    threat: 'MODERATE',
    resources: 'HIGH',
    services: ['refuel', 'exploration', 'repairs', 'trade'],
    tradeType: 'luxury',
    primaryColor: '#ff0000',
    secondaryColor: '#ffff00',
    hasRings: false,
    description: 'Vertical megacity stretching into the clouds. Triad-controlled sectors. Street food vendors serve synthetic noodles. Data havens hide in the lower levels. Hacker collectives operate from abandoned arcologies.',
    scanDescription: 'SCAN: Vertical city structure detected. Height: 5 kilometers. Triad territories mapped. Data havens: NUMEROUS. Street food vendors: EVERYWHERE. Warning: Hacker collective activity detected. Recommend data security measures.'
  },
  {
    id: 1305,
    name: 'Detroit Reclamation Zone',
    type: 'planet',
    distance: 25,
    coordinates: { x: 65, y: 45, z: -3 },
    threat: 'HIGH',
    resources: 'MEDIUM',
    services: ['refuel', 'exploration', 'repairs', 'trade', 'weapons', 'upgrades'],
    tradeType: 'industrial',
    primaryColor: '#888888',
    secondaryColor: '#ffaa00',
    hasRings: false,
    description: 'Augmentation capital of the sector. Sarif Industries headquarters. Mechanical apartheid divides augmented and natural humans. Resistance safe houses hide in abandoned factories. "I never asked for this."',
    scanDescription: 'SCAN: Industrial zone detected. Augmentation clinics: EXTENSIVE. Sarif Industries presence confirmed. Resistance activity: MODERATE. Warning: Mechanical apartheid enforcement active. Augmented individuals may face discrimination.'
  },
  {
    id: 1306,
    name: 'Elysium Station',
    type: 'station',
    distance: 32,
    coordinates: { x: 25, y: 45, z: 10 },
    threat: 'SAFE',
    resources: 'VERY HIGH',
    services: ['refuel', 'repairs', 'trade', 'weapons', 'upgrades', 'modules'],
    tradeType: 'luxury',
    primaryColor: '#ffffff',
    secondaryColor: '#00ffff',
    hasRings: true,
    description: 'Orbital paradise for the ultra-wealthy. Medical bays offer full body restoration. Luxury goods and designer cybernetics. The 1% live here while the 99% struggle below. Stark class divide enforced by armed security.',
    scanDescription: 'SCAN: Luxury orbital habitat detected. Population: 100,000 elite. Medical facilities: STATE-OF-THE-ART. Security level: MAXIMUM. Warning: Entry restricted to verified wealthy individuals. Recommend proper credentials.'
  },
  {
    id: 1307,
    name: 'The Sprawl',
    type: 'station',
    distance: 20,
    coordinates: { x: 50, y: 65, z: -8 },
    threat: 'MODERATE',
    resources: 'HIGH',
    services: ['refuel', 'repairs', 'trade', 'upgrades', 'modules'],
    tradeType: 'industrial',
    primaryColor: '#0088ff',
    secondaryColor: '#00ffff',
    hasRings: false,
    description: 'Cyberspace access hub. Matrix connection points everywhere. AI entities roam the digital realm. Data heists planned in virtual reality. "The matrix has its roots in primitive arcade games."',
    scanDescription: 'SCAN: Cyberspace hub detected. Matrix access points: UNLIMITED. AI entities: NUMEROUS. Data heist activity: HIGH. Warning: Rogue AI presence detected. Recommend ICE protocols and firewall updates.'
  },
  {
    id: 1308,
    name: 'Akira Crater',
    type: 'planet',
    distance: 30,
    coordinates: { x: 35, y: 35, z: 5 },
    threat: 'VERY HIGH',
    resources: 'LOW',
    services: ['refuel', 'exploration', 'repairs'],
    tradeType: 'remote',
    primaryColor: '#ff0000',
    secondaryColor: '#ffffff',
    hasRings: false,
    description: 'Ruins of Neo-Tokyo after the psychic explosion. Government experiments on espers continue in underground facilities. Biker gangs race through radioactive streets. "TETSUOOOOO!" "KANEDAAAAA!"',
    scanDescription: 'SCAN: Catastrophic destruction detected. Psychic energy signatures: EXTREME. Government research facilities: ACTIVE. Biker gang activity: HIGH. Warning: Psychic phenomena may cause hallucinations and reality distortions.'
  },
  {
    id: 1309,
    name: 'Synthetic Wasteland',
    type: 'planet',
    distance: 28,
    coordinates: { x: 60, y: 30, z: -10 },
    threat: 'HIGH',
    resources: 'LOW',
    services: ['refuel', 'exploration', 'repairs'],
    tradeType: 'remote',
    primaryColor: '#666666',
    secondaryColor: '#ff00ff',
    hasRings: false,
    description: 'Failed android colony. Rogue AI presence detected. Synths seeking humanity wander the wastes. Scrap salvage operations. Existential questions echo through abandoned cities. "Do androids dream of electric sheep?"',
    scanDescription: 'SCAN: Android colony ruins detected. Rogue AI signatures: CONFIRMED. Synth population: UNKNOWN. Scrap value: HIGH. Warning: Philosophical debates may cause existential crises. Recommend emotional dampeners.'
  },
  {
    id: 1310,
    name: 'Digital Singularity',
    type: 'blackhole',
    distance: 26000,
    coordinates: { x: 42, y: 48, z: -12 },
    threat: 'EXTREME',
    resources: 'GRAVITATIONAL ANOMALY',
    services: [],
    tradeType: 'none',
    primaryColor: '#000000',
    secondaryColor: '#00ffff',
    hasRings: true,
    description: 'Supermassive black hole at the center of Neon Metropolis. Warps spacetime, explaining why distant systems are reachable. Corporate scientists study its properties. Some say an AI achieved singularity and became the black hole itself.',
    scanDescription: 'SCAN: Supermassive black hole detected. Mass: 4 million solar masses. Hawking radiation: DETECTED. Spacetime compression: EXTREME. Warning: Approach is fatal. Corporate research vessels lost. AI singularity theories unconfirmed.'
  }
];

// Star system positions on the map
export const starPositions = starSystems.reduce((acc, system) => {
  acc[system.id] = system.coordinates;
  return acc;
}, {});